import { createBrowserRouter, RouterProvider, Routes, Route } from "react-router-dom";
import { SessionContextProvider } from "@supabase/auth-helpers-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { supabase } from "./integrations/supabase/client";
import { Toaster } from "sonner";
import "./App.css";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import Landing from "./pages/Landing";
import ScheduleInvoice from "./pages/ScheduleInvoice";
import Login from "./pages/Login";
import SignUp from "./pages/SignUp";
import ResetPassword from "./pages/ResetPassword";
import ForgotPassword from "./pages/ForgotPassword";
import AccountSettings from "./pages/AccountSettings";
import Templates from "./pages/Templates";
import Customers from "./pages/Customers";
import ContactUs from "./pages/ContactUs";
import FAQ from "./pages/FAQ";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import SetupAccount from "./pages/SetupAccount";
import PaymentSuccess from "./pages/PaymentSuccess";
import { StripeCallback } from "@/components/stripe/StripeCallback";

const router = createBrowserRouter([
  {
    path: "/",
    element: <Landing />,
  },
  {
    path: "/privacy-policy",
    element: <PrivacyPolicy />,
  },
  {
    path: "/terms-of-service",
    element: <TermsOfService />,
  },
  {
    path: "/payment-success",
    element: <PaymentSuccess />,
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/signup",
    element: <SignUp />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPassword />,
  },
  {
    path: "/reset-password",
    element: <ResetPassword />,
  },
  {
    path: "/setup-account",
    element: (
      <ProtectedRoute redirectTo="/login">
        <SidebarProvider>
          <div className="min-h-screen flex w-full">
            <AppSidebar />
            <main className="flex-1">
              <SetupAccount />
            </main>
          </div>
        </SidebarProvider>
      </ProtectedRoute>
    ),
  },
  {
    path: "/stripe/callback",
    element: (
      <ProtectedRoute redirectTo="/login">
        <SidebarProvider>
          <div className="min-h-screen flex w-full">
            <AppSidebar />
            <main className="flex-1">
              <StripeCallback />
            </main>
          </div>
        </SidebarProvider>
      </ProtectedRoute>
    ),
  },
  {
    path: "/dashboard",
    element: (
      <ProtectedRoute redirectTo="/login">
        <SidebarProvider>
          <div className="min-h-screen flex w-full">
            <AppSidebar />
            <main className="flex-1">
              <ScheduleInvoice />
            </main>
          </div>
        </SidebarProvider>
      </ProtectedRoute>
    ),
  },
  {
    path: "/account_settings",
    element: (
      <ProtectedRoute redirectTo="/login">
        <SidebarProvider>
          <div className="min-h-screen flex w-full">
            <AppSidebar />
            <main className="flex-1">
              <AccountSettings />
            </main>
          </div>
        </SidebarProvider>
      </ProtectedRoute>
    ),
  },
  {
    path: "/contact_us",
    element: (
      <ProtectedRoute redirectTo="/login">
        <SidebarProvider>
          <div className="min-h-screen flex w-full">
            <AppSidebar />
            <main className="flex-1">
              <ContactUs />
            </main>
          </div>
        </SidebarProvider>
      </ProtectedRoute>
    ),
  },
  {
    path: "/faq",
    element: (
      <ProtectedRoute redirectTo="/login">
        <SidebarProvider>
          <div className="min-h-screen flex w-full">
            <AppSidebar />
            <main className="flex-1">
              <FAQ />
            </main>
          </div>
        </SidebarProvider>
      </ProtectedRoute>
    ),
  },
  {
    path: "/templates",
    element: (
      <ProtectedRoute redirectTo="/login">
        <SidebarProvider>
          <div className="min-h-screen flex w-full">
            <AppSidebar />
            <main className="flex-1">
              <Templates />
            </main>
          </div>
        </SidebarProvider>
      </ProtectedRoute>
    ),
  },
  {
    path: "/customers",
    element: (
      <ProtectedRoute redirectTo="/login">
        <SidebarProvider>
          <div className="min-h-screen flex w-full">
            <AppSidebar />
            <main className="flex-1">
              <Customers />
            </main>
          </div>
        </SidebarProvider>
      </ProtectedRoute>
    ),
  },
  {
    path: "*",
    element: <NotFound />,
  },
]);

// Create a client
const queryClient = new QueryClient();

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SessionContextProvider supabaseClient={supabase}>
        <RouterProvider router={router} />
        <Toaster position="top-right" />
      </SessionContextProvider>
    </QueryClientProvider>
  );
}

export default App;
